/**
 * Auth Context for Trade Loop v2.0
 *
 * Cookie-based authentication - no token storage in localStorage
 * Cookies are managed automatically by the browser and server
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as authService from '@/services/authService';
import type { User, LoginResponse, Impersonator } from '@/services/authService';
import { supabase } from '@/lib/supabase';
import { NativeSessionVerificationError } from '@/services/nativeGoogleAuth';
import { clearSessionId, restoreSessionId } from '@/services/nativeSession';
import { syncPendingPushToken } from '@/services/capacitor/pushNotifications';
import { clearAppBadge } from '@/services/capacitor/appBadge';
import { clearAllNotifications } from '@/services/capacitor/pushNotifications';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionReady: boolean; // Indicates Supabase Realtime auth is set
  /**
   * Non-null only while an admin is impersonating the current user. Comes
   * straight from the server on /auth/me — there is no client-side way to set
   * it, so a tampered client cannot fake (or hide) an impersonation session.
   */
  impersonator: Impersonator | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (data: authService.SignupData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  handleAuthCallback: (
    accessToken: string,
    refreshToken: string,
    extras?: authService.AuthCallbackExtras
  ) => Promise<void>;
  /** Native-only: exchange a Google id_token for a session (see authService). */
  signInWithGoogleNative: (data: authService.NativeGoogleSignInData) => Promise<void>;
  /** Native-only: exchange an Apple id_token for a session (see authService). */
  signInWithAppleNative: (data: authService.NativeAppleSignInData) => Promise<void>;
  verifyOtp: (email: string, token: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Transform API response to User object
 * Merges Supabase Auth user with user_profiles data
 */
const transformToUser = (response: LoginResponse): User => {
  const { user, profile } = response;

  // Parse JSONB fields if they're strings
  const parseJsonField = (field: any): string[] | undefined => {
    if (!field) return undefined;
    if (Array.isArray(field)) return field;
    if (typeof field === 'string') {
      try {
        return JSON.parse(field);
      } catch {
        return undefined;
      }
    }
    return undefined;
  };

  // Profile now comes from API in camelCase (transformed on backend)
  // User interface also uses camelCase - direct mapping
  return {
    id: user?.id || profile?.id,
    email: user?.email || profile?.email,
    firstName: profile?.firstName || undefined,
    lastName: profile?.lastName || undefined,
    fullName: profile?.fullName || undefined,
    profileImageUrl: profile?.profileImageUrl || undefined,
    companyLogoUrl: profile?.companyLogoUrl || undefined,
    role: response.role?.role || response.role?.name || undefined,
    isAdmin: response.role?.isAdmin === true,
    isSuperAdmin: response.role?.role === 'Super Admin' || response.role?.name === 'Super Admin',
    phone: user?.phone || profile?.phone || undefined,
    phoneConfirmedAt: user?.phone_confirmed_at || user?.phoneConfirmedAt || undefined,
    company: profile?.company || undefined,
    companyAddress: profile?.companyAddress || undefined,
    trade: profile?.trade || undefined,
    location: profile?.location || undefined,
    industries: parseJsonField(profile?.industries),
    marketTypes: parseJsonField(profile?.marketTypes),
    serviceAreas: parseJsonField(profile?.serviceAreas),
    personalBio: profile?.personalBio || undefined,
    companyBio: profile?.companyBio || undefined,
    subscriptionPlan: profile?.subscriptionPlan || undefined,
    subscriptionStatus: profile?.subscriptionStatus || undefined,
    onboardingComplete: profile?.onboardingComplete || false,
    initialOnboardingCompleted: profile?.initialOnboardingCompleted || false,
    trigPrimaryTradeName: profile?.trigPrimaryTradeName || undefined,
    primaryTradeId: profile?.primaryTradeId || undefined,
    organizationMembership: response.organizationMembership || null,
    showEmailToConnections: profile?.showEmailToConnections,
    showPhoneToConnections: profile?.showPhoneToConnections,
    timezone: profile?.timezone || undefined,
    createdAt: profile?.createdAt || undefined,
  };
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [impersonator, setImpersonator] = useState<Impersonator | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const queryClient = useQueryClient();

  // Realtime token cache - reduces backend calls for token fetching
  // Tokens are cached for 1 hour with a 60-second buffer before expiry
  const realtimeTokenCache = useRef<{
    token: string;
    expiresAt: number;
  } | null>(null);

  // Guards the OAuth / email-confirmation callback race. When the browser lands
  // on /auth/callback, AuthProvider mounts and fires checkAuth() (GET /auth/me)
  // at the same time AuthCallbackPage establishes the session via
  // handleAuthCallback() (POST /auth/callback, which sets the session cookie).
  // The /auth/me probe runs BEFORE that cookie exists, so if it resolves AFTER
  // the callback its 401 would clobber the freshly-authenticated user back to
  // null — and ProtectedRoute would then bounce a genuinely logged-in user to
  // /login (the Google-signup bug). Once a callback establishes the session we
  // flip this flag so the stale probe can't overwrite it.
  const sessionEstablishedRef = useRef(false);

  // Bumped on every logout. fetchAndSetRealtimeToken captures the value it started with and
  // drops its result if it no longer matches, because the mount-time fetch is no longer
  // awaited (see checkAuth) and can therefore still be in flight when the user signs out —
  // without this it would re-authenticate the Realtime socket and flip sessionReady back to
  // true for an account that just logged out.
  const authEpochRef = useRef(0);

  // Fetch access token from backend and set Supabase Realtime auth.
  // The access token is stored in an httpOnly cookie (inaccessible to JS).
  // The backend /auth/realtime-token endpoint reads the cookie and returns only
  // the access token — the refresh token is NEVER exposed to frontend JS.
  // supabase.realtime.setAuth() only needs an access token (no refresh token).
  //
  // OPTIMIZATION: Token caching with 1-hour expiry reduces backend calls
  const fetchAndSetRealtimeToken = useCallback(async (): Promise<boolean> => {
    const epoch = authEpochRef.current;
    // A logout that lands while this is in flight wins: nothing below may re-arm the
    // Realtime socket for a session the user has already ended.
    const isStale = () => authEpochRef.current !== epoch;

    try {
      // Check cache first - use cached token if valid (not expired within 60 seconds)
      const now = Date.now();
      const cache = realtimeTokenCache.current;

      if (cache && cache.expiresAt > now) {
        console.log('[Auth] ⚡ Using cached realtime token (expires in', Math.round((cache.expiresAt - now) / 1000), 'seconds)');
        supabase.realtime.setAuth(cache.token);
        setSessionReady(true);
        return true;
      }

      // Cache expired or doesn't exist - fetch new token
      const response = await fetch('/api/auth/realtime-token', {
        method: 'POST',
        credentials: 'include',
      });

      if (isStale()) return false;

      if (!response.ok) {
        // Server handles token refresh internally — if session is valid, this works.
        // If 401, session is dead — no client-side retry needed.
        console.warn('[Auth] ⚠️ Failed to fetch realtime token:', response.status);
        setSessionReady(false);
        return false;
      }

      const data = await response.json();
      const accessToken = data.accessToken || data.access_token;

      if (isStale()) return false;

      if (!accessToken) {
        console.warn('[Auth] ⚠️ No access token in realtime-token response. Full response:', data);
        setSessionReady(false);
        return false;
      }

      // Cache token for 1 hour (3600000ms) with 60-second buffer before expiry
      const expiresAt = Date.now() + 3600000 - 60000;
      realtimeTokenCache.current = { token: accessToken, expiresAt };

      // Authenticate Realtime WebSocket channel only — no refresh token needed
      supabase.realtime.setAuth(accessToken);
      console.log('[Auth] ✅ Supabase Realtime auth set (cached until', new Date(expiresAt).toLocaleTimeString(), ')');
      setSessionReady(true);
      return true;
    } catch (error) {
      if (isStale()) return false;
      console.error('[Auth] ❌ Exception fetching realtime token:', error);
      setSessionReady(false);
      return false;
    }
  }, []);

  // Check if user is authenticated on mount
  // Includes timeout to prevent app from being stuck in loading state forever
  const checkAuth = useCallback(async () => {
    // Safety timeout - ensure we never stay in loading state for more than 10 seconds
    const timeoutId = setTimeout(() => {
      console.error('[Auth] ⚠️ Auth check timed out after 10 seconds');
      setIsLoading(false);
      if (!sessionEstablishedRef.current) setUser(null);
    }, 10000);

    try {
      const response = await authService.getCurrentUser();
      // A concurrent auth callback (OAuth / email confirmation) already
      // established an authoritative session — don't overwrite it with this
      // (possibly stale) mount-time probe. See sessionEstablishedRef above.
      if (sessionEstablishedRef.current) return;
      console.log('[Auth] getCurrentUser response:', response);
      console.log('[Auth] organizationMembership from response:', response.organizationMembership);
      const transformedUser = transformToUser(response);
      console.log('[Auth] Transformed user:', transformedUser);
      console.log('[Auth] organizationMembership in user:', transformedUser.organizationMembership);
      setUser(transformedUser);
      setImpersonator(response.impersonator ?? null);

      // Set Supabase Realtime auth using the access token from httpOnly cookies
      // (The backend /auth/realtime-token endpoint reads the cookie and returns the token)
      //
      // NOT awaited, deliberately. This is the app's cold-start path: `isLoading` only
      // clears in the finally below, ProtectedRoute renders a spinner until it does, and
      // every page's queries are gated on `enabled: !!user` — so awaiting this put a second
      // serial round trip (~230ms measured in production) in front of the first byte of
      // page data on EVERY route. Nothing here blocks rendering: the token only feeds
      // supabase.realtime.setAuth, and the handful of Realtime subscriptions that need it
      // are already gated on `sessionReady`, so they simply arm a moment later. Failures are
      // swallowed inside fetchAndSetRealtimeToken (it never throws) and must not log the
      // user out; a logout that beats it home is handled by the authEpochRef guard.
      void fetchAndSetRealtimeToken();
    } catch (error) {
      // Server handles token refresh internally via session_id cookie.
      // If /api/auth/me returns 401, the session is dead — no client-side retry.
      // But if an auth callback established the session while this probe was in
      // flight, the 401 is stale (it ran before the cookie existed) — skip the
      // clear so we don't clobber the authenticated user into a /login bounce.
      if (sessionEstablishedRef.current) return;
      // Not authenticated or session expired — clear user state
      console.log('[Auth] User not authenticated or session expired');
      setUser(null);
      setImpersonator(null);
      setSessionReady(false);
    } finally {
      clearTimeout(timeoutId);
      setIsLoading(false);
    }
  }, [fetchAndSetRealtimeToken]);

  useEffect(() => {
    // Native keeps its session id in the Keychain rather than a cookie, so it
    // has to be loaded before the first authenticated request — otherwise a
    // returning user's /auth/me probe goes out unauthenticated and they get
    // bounced to /login despite having a live session. No-op (and synchronous
    // in effect) on web.
    void restoreSessionId()
      .then((result) => {
        // 'missing' is the ordinary signed-out case. The other two mean the
        // secure store itself is broken, which must not be mistaken for it —
        // that conflation is what hid a native plugin missing from the iOS
        // binary. LoginPage surfaces this; see nativeSession.getStoreFailure().
        if (result.status === 'unavailable' || result.status === 'corrupt') {
          console.error(
            `[Auth] Native secure storage ${result.status}; the session cannot persist across restarts.`,
            result.error
          );
        }
      })
      .finally(() => checkAuth());
  }, [checkAuth]);

  // 🔄 AUTO-REFRESH: Re-fetch the Supabase access token every 5 minutes so the
  // Realtime WebSocket stays authenticated. Access tokens expire in ~1h; rotation
  // is driven entirely by this poll (it's the sole caller of /auth/realtime-token,
  // which is the sole caller of getSupabaseAccessToken on the backend).
  //
  // Interval math: backend rotation fires when the cached access token has ≤60s
  // remaining. With 5-min polls the 12th tick (T=3600s) lands at expiry and
  // triggers rotation — staleness window ≈ network round-trip (<1s). A 9-min
  // interval would miss expiry by ~3 min, leaving the WebSocket unauthed.
  useEffect(() => {
    if (!user) {
      console.log('[Auth] ⏸️  Realtime token refresh not active (no user logged in)');
      return;
    }

    console.log('[Auth] 🚀 Setting up Realtime token refresh interval');

    const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
    const intervalId = setInterval(async () => {
      console.log('[Auth] 🔄 Refreshing Realtime token...');
      await fetchAndSetRealtimeToken();
    }, REFRESH_INTERVAL_MS);

    return () => {
      console.log('[Auth] 🧹 Clearing Realtime token refresh interval');
      clearInterval(intervalId);
    };
  }, [user, fetchAndSetRealtimeToken]);

  // 📲 PUSH TOKEN: flush a device token the server never received.
  //
  // Push registration is kicked off by `initializeCapacitor()` at module scope in main.tsx, so
  // the `registration` event can — and on iOS reliably does — land before this provider has
  // loaded the session id out of the Keychain. That token POST then goes out unauthenticated and
  // the API answers 401, leaving the device registered under whatever token it last managed to
  // send. Found 2026-08-16: an iPhone stuck on a token from 2026-08-01, sixteen days of pushes
  // accepted by FCM and delivered nowhere, with no error surfaced anywhere.
  //
  // This is the other half of the fix (see `syncPendingPushToken`): the moment a user exists, the
  // session id is loaded, so retry. Keyed on `user` rather than mount so it covers a fresh login
  // as well as a restored session, and it no-ops unless a sync is actually outstanding.
  useEffect(() => {
    if (!user) return;
    void syncPendingPushToken();
  }, [user]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      // Authenticate with login endpoint (sets httpOnly cookies)
      await authService.login(email, password);

      // Fetch complete user data including organizationMembership
      const meResponse = await authService.getCurrentUser();
      console.log('[Auth] Login: /auth/me response:', meResponse);
      console.log('[Auth] Login: organizationMembership:', meResponse.organizationMembership);

      const transformedUser = transformToUser(meResponse);
      console.log('[Auth] Login: transformed user:', transformedUser);

      setUser(transformedUser);
      setImpersonator(meResponse.impersonator ?? null);

      // Set Supabase Realtime auth using access token from httpOnly cookie
      await fetchAndSetRealtimeToken();
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (data: authService.SignupData) => {
    setIsLoading(true);
    try {
      // Call signup but DON'T set user - email confirmation required first
      await authService.signup(data);
      // User will be set after they confirm email and log in
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // Clear client state FIRST — before any awaited network call — so the
    // SessionExpirationHandler redirect path works even when the logout POST
    // fails (network error, aborted by queryClient.cancelQueries(), CORS, etc.).
    // If setUser(null) is skipped, isAuthenticated stays true and LoginPage
    // bounces the user right back to /dashboard, causing the session-expired loop.
    setUser(null);
    setImpersonator(null);
    setSessionReady(false);
    sessionEstablishedRef.current = false;
    // Invalidates any realtime-token fetch still in flight (see authEpochRef).
    authEpochRef.current += 1;
    realtimeTokenCache.current = null;
    supabase.realtime.setAuth(null);
    queryClient.clear();
    // Native holds the session id itself, so clearing cookies server-side is not
    // enough — drop the Keychain copy too, or the next launch restores a session
    // the user just ended. No-op on web.
    void clearSessionId();
    // The badge belongs to the account, not the device. `useAppBadge` stops writing the
    // moment AppLayout unmounts, so whatever number was on the icon would otherwise
    // survive the logout and greet the next person to sign in on this phone.
    void clearAppBadge();
    void clearAllNotifications();

    setIsLoading(true);
    try {
      await authService.logout();
    } catch (error) {
      console.warn('[Auth] Server logout failed (state already cleared):', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authService.getCurrentUser();
      setUser(transformToUser(response));
      setImpersonator(response.impersonator ?? null);
    } catch {
      // Server handles token refresh internally — if 401, session is dead.
      setUser(null);
      setImpersonator(null);
    }
  };

  const handleAuthCallback = async (
    accessToken: string,
    refreshToken: string,
    extras?: authService.AuthCallbackExtras
  ) => {
    setIsLoading(true);
    try {
      const response = await authService.handleAuthCallback(accessToken, refreshToken, extras);
      // Mark the session authoritative BEFORE setUser so a concurrent mount-time
      // checkAuth() probe (fired before the cookie existed) can't clobber it.
      sessionEstablishedRef.current = true;
      setUser(transformToUser(response));
      // Set Supabase Realtime auth using access token from httpOnly cookie
      await fetchAndSetRealtimeToken();
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Native Google sign-in (bundled Capacitor builds). The backend endpoint
   * establishes the session cookie and returns the same payload as
   * /api/auth/callback, but we re-read /auth/me afterwards for the same reason
   * login() does: the callback payload has no organizationMembership.
   */
  const signInWithGoogleNative = async (data: authService.NativeGoogleSignInData) => {
    setIsLoading(true);
    try {
      const exchange = await authService.signInWithGoogleNative(data);

      // Past this line the backend HAS created a session and set the cookie, so
      // a failure below is a transport fault, not a sign-in fault. Keep the two
      // distinguishable — collapsing them reports "Google sign-in failed" for a
      // sign-in Google actually completed.
      let response = exchange;
      try {
        response = await authService.getCurrentUser();
      } catch (firstError) {
        // One retry, because the two failure modes look identical from here and
        // only one of them is fatal: if the native cookie jar simply had not
        // committed the Set-Cookie by the time this fired, a moment later works;
        // if the cookie is never replayed on this platform, this fails too and
        // we report it as a transport fault rather than a sign-in failure.
        await new Promise((resolve) => setTimeout(resolve, 400));
        try {
          response = await authService.getCurrentUser();
        } catch (error) {
          throw new NativeSessionVerificationError(
            (error as { status?: number } | null)?.status,
            (error as { data?: { code?: string } } | null)?.data?.code,
            (error as { message?: string } | null)?.message,
            firstError
          );
        }
      }

      // Mark the session authoritative BEFORE setUser so a concurrent mount-time
      // checkAuth() probe (fired before the cookie existed) can't clobber it.
      sessionEstablishedRef.current = true;
      setUser(transformToUser(response));
      setImpersonator(response.impersonator ?? null);

      // Set Supabase Realtime auth using access token from httpOnly cookie
      await fetchAndSetRealtimeToken();
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Native Apple sign-in (bundled Capacitor builds). Identical contract to
   * {@link signInWithGoogleNative}, including the retry that keeps a transport
   * fault distinguishable from a sign-in failure.
   */
  const signInWithAppleNative = async (data: authService.NativeAppleSignInData) => {
    setIsLoading(true);
    try {
      const exchange = await authService.signInWithAppleNative(data);

      // Past this line the backend HAS created a session and set the cookie, so
      // a failure below is a transport fault, not a sign-in fault.
      let response = exchange;
      try {
        response = await authService.getCurrentUser();
      } catch (firstError) {
        await new Promise((resolve) => setTimeout(resolve, 400));
        try {
          response = await authService.getCurrentUser();
        } catch (error) {
          throw new NativeSessionVerificationError(
            (error as { status?: number } | null)?.status,
            (error as { data?: { code?: string } } | null)?.data?.code,
            (error as { message?: string } | null)?.message,
            firstError
          );
        }
      }

      // Mark the session authoritative BEFORE setUser so a concurrent mount-time
      // checkAuth() probe (fired before the cookie existed) can't clobber it.
      sessionEstablishedRef.current = true;
      setUser(transformToUser(response));
      setImpersonator(response.impersonator ?? null);

      // Set Supabase Realtime auth using access token from httpOnly cookie
      await fetchAndSetRealtimeToken();
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async (email: string, token: string) => {
    setIsLoading(true);
    try {
      const response = await authService.verifyOtp(email, token);
      setUser(transformToUser(response));
      // Set Supabase Realtime auth using access token from httpOnly cookie
      await fetchAndSetRealtimeToken();
    } finally {
      setIsLoading(false);
    }
  };

  const resendVerification = async (email: string) => {
    await authService.resendVerification(email);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    sessionReady,
    impersonator,
    login,
    signup,
    logout,
    refreshUser,
    handleAuthCallback,
    signInWithGoogleNative,
    signInWithAppleNative,
    verifyOtp,
    resendVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
